/**
 * ScreenListPanel
 *
 * Panel for listing, creating, and managing ESP32 screens.
 * Can be used in workflow editor sidebar.
 */

import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus,
  MoreVertical,
  Pencil,
  Copy,
  Trash2,
  Monitor,
  Loader2,
  Search,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useScreenStore, Screen } from '@/stores/screens/screen-store';

interface ScreenListPanelProps {
  className?: string;
  onScreenSelect?: (screen: Screen) => void;
}

export const ScreenListPanel: React.FC<ScreenListPanelProps> = ({
  className,
  onScreenSelect,
}) => {
  const {
    screens,
    isLoading,
    error,
    fetchScreens,
    deleteScreen,
    duplicateScreen,
    openBuilder,
  } = useScreenStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [screenToDelete, setScreenToDelete] = useState<Screen | null>(null);

  // Fetch screens on mount
  useEffect(() => {
    fetchScreens();
  }, [fetchScreens]);

  // Filter screens by search
  const filteredScreens = screens.filter((screen) =>
    screen.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    screen.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle create new screen
  const handleCreateNew = () => {
    openBuilder();
  };

  // Handle edit screen
  const handleEdit = (screen: Screen) => {
    openBuilder(screen);
  };

  // Handle duplicate
  const handleDuplicate = async (screen: Screen) => {
    const duplicated = await duplicateScreen(screen.id);
    if (duplicated) {
      toast.success('Screen duplicated', { description: duplicated.name });
    } else {
      toast.error('Failed to duplicate screen');
    }
  };

  // Handle delete confirmation
  const handleDeleteClick = (screen: Screen) => {
    setScreenToDelete(screen);
    setDeleteDialogOpen(true);
  };

  // Handle delete confirmed
  const handleDeleteConfirm = async () => {
    if (!screenToDelete) return;

    const success = await deleteScreen(screenToDelete.id);
    if (success) {
      toast.success('Screen deleted');
    } else {
      toast.error('Failed to delete screen');
    }

    setDeleteDialogOpen(false);
    setScreenToDelete(null);
  };

  // Handle screen click
  const handleScreenClick = (screen: Screen) => {
    if (onScreenSelect) {
      onScreenSelect(screen);
    } else {
      handleEdit(screen);
    }
  };

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Header */}
      <div className="p-3 border-b flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Monitor className="w-4 h-4" />
          Screens
        </h3>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => fetchScreens()}
            disabled={isLoading}
          >
            <RefreshCw className={cn('w-4 h-4', isLoading && 'animate-spin')} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleCreateNew}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="p-2 border-b">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search screens..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 pl-8 text-sm"
          />
        </div>
      </div>

      {/* Screen list */}
      <ScrollArea className="flex-1">
        {isLoading && screens.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="p-4 text-center text-sm text-destructive">{error}</div>
        ) : filteredScreens.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            {searchQuery ? 'No screens match your search' : 'No screens yet'}
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {filteredScreens.map((screen) => (
              <ScreenListItem
                key={screen.id}
                screen={screen}
                onClick={() => handleScreenClick(screen)}
                onEdit={() => handleEdit(screen)}
                onDuplicate={() => handleDuplicate(screen)}
                onDelete={() => handleDeleteClick(screen)}
              />
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Quick add button at bottom */}
      <div className="p-2 border-t">
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={handleCreateNew}
        >
          <Plus className="w-4 h-4 mr-2" />
          New Screen
        </Button>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Screen</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{screenToDelete?.name}"? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Individual screen list item
interface ScreenListItemProps {
  screen: Screen;
  onClick: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

const ScreenListItem: React.FC<ScreenListItemProps> = ({
  screen,
  onClick,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  return (
    <div
      className="group flex items-center gap-2 p-2 rounded-md hover:bg-accent cursor-pointer"
      onClick={onClick}
    >
      {/* Preview thumbnail */}
      <div
        className="w-10 h-8 rounded border flex-shrink-0"
        style={{ backgroundColor: screen.definition.backgroundColor }}
      >
        {/* Mini representation of elements */}
        <div className="relative w-full h-full overflow-hidden">
          {screen.definition.elements.slice(0, 5).map((el) => (
            <div
              key={el.id}
              className="absolute bg-white/20 rounded-sm"
              style={{
                left: `${(el.x / 320) * 100}%`,
                top: `${(el.y / 240) * 100}%`,
                width: `${(el.width / 320) * 100}%`,
                height: `${(el.height / 240) * 100}%`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{screen.name}</div>
        <div className="text-xs text-muted-foreground truncate">
          {screen.definition.elements.length} elements • v{screen.version}
        </div>
      </div>

      {/* Actions */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(); }}>
            <Pencil className="w-4 h-4 mr-2" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDuplicate(); }}>
            <Copy className="w-4 h-4 mr-2" />
            Duplicate
          </DropdownMenuItem>
          <Separator className="my-1" />
          <DropdownMenuItem
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default ScreenListPanel;
